import React, { useState } from "react";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

export function SendAllEmailsTest() {
  const { sendEmail, isSending } = useTransactionalEmail();
  const [sentEmails, setSentEmails] = useState<string[]>([]);

  const testEmail = "brian@grandpal.co";

  const emailTests = [
    {
      type: 'waitlist_confirmation' as const,
      name: 'Waitlist Confirmation',
      data: {
        firstName: "Brian",
        email: testEmail,
        nursingHomes: ["Sunset Manor", "Green Valley Care", "Riverside Lodge"],
        dashboardUrl: "https://grandpal.co/dashboard"
      }
    },
    {
      type: 'nursing_home_leads' as const,
      name: 'Nursing Home Leads',
      data: {
        firstName: "Brian",
        email: testEmail,
        nursingHomes: ["Oak Tree Care Home", "Meadowbrook Manor"],
        dashboardUrl: "https://grandpal.co/dashboard"
      }
    },
    {
      type: 'welcome' as const,
      name: 'Welcome Email',
      data: {
        firstName: "Brian",
        email: testEmail,
        dashboardUrl: "https://grandpal.co/dashboard"
      }
    },
    {
      type: 'claim_request' as const,
      name: 'Claim Request (Admin)',
      data: {
        adminName: "Brian",
        email: testEmail,
        nursingHomeName: "Hillside Care Centre",
        claimantName: "John Smith",
        claimantEmail: "john.smith@email.com",
        claimantRole: "Manager",
        reviewUrl: "https://grandpal.co/admin/claims"
      }
    },
    {
      type: 'nursing_home_inquiry' as const,
      name: 'Nursing Home Inquiry',
      data: {
        email: testEmail,
        nursingHomeName: "Peaceful Gardens Care",
        county: "Cork",
        message: "A family is interested in your nursing home and would like to learn more about availability and services.",
        claimLink: "https://grandpal.co/claim",
        leadId: "test-lead-123"
      }
    },
    {
      type: 'guide_download' as const,
      name: 'Guide Download',
      data: {
        firstName: "Brian",
        email: testEmail,
        guideTitle: "Complete Guide to Finding Nursing Homes in Ireland",
        guideUrl: "https://grandpal.co/guides/finding-nursing-home",
        waitlistUrl: "https://grandpal.co/waitlist"
      }
    },
    {
      type: 'inquiry_confirmation' as const,
      name: 'Inquiry Confirmation',
      data: {
        firstName: "Brian",
        email: testEmail,
        nursingHomeName: "Sunset Manor Care Home",
        county: "Cork",
        dashboardUrl: "https://grandpal.co/dashboard",
        signUpUrl: "https://grandpal.co/signup"
      }
    },
    {
      type: 'expert_application_confirmation' as const,
      name: 'Expert Application Confirmation',
      data: {
        firstName: "Brian",
        email: testEmail,
        professionalTitle: "Care Placement Specialist",
        interviewUrl: "https://calendly.com/grandpal/expert-interview"
      }
    },
    {
      type: 'info_request' as const,
      name: 'Info Request',
      data: {
        email: testEmail,
        nursingHomeName: "Valley View Care Home",
        county: "Cork",
        message: "Families are looking for more information about your nursing home.",
        claimLink: "https://grandpal.co/claim",
        leadId: "test-info-request-123"
      }
    }
  ];

  const sendAllEmails = async () => {
    setSentEmails([]);
    let successCount = 0;
    
    for (const emailTest of emailTests) {
      try {
        await sendEmail({
          type: emailTest.type,
          data: emailTest.data
        });
        setSentEmails(prev => [...prev, emailTest.name]);
        successCount++;
        toast.success(`✅ ${emailTest.name} sent successfully`);
        
        // Add a small delay between emails to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`Failed to send ${emailTest.name}:`, error);
        toast.error(`❌ Failed to send ${emailTest.name}: ${error.message}`);
      }
    }
    
    toast.success(`🎉 Completed! ${successCount}/${emailTests.length} emails sent to ${testEmail}`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send All Email Templates</CardTitle>
        <CardDescription>
          Send one of every email template to {testEmail} for testing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
          {emailTests.map((emailTest) => (
            <div
              key={emailTest.type}
              className={`p-2 rounded border ${
                sentEmails.includes(emailTest.name)
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              {sentEmails.includes(emailTest.name) ? '✅' : '📧'} {emailTest.name}
            </div>
          ))}
        </div>
        
        <Button 
          onClick={sendAllEmails} 
          disabled={isSending}
          className="w-full"
        >
          {isSending ? 'Sending Emails...' : `Send All ${emailTests.length} Email Templates`}
        </Button>
        
        {sentEmails.length > 0 && (
          <div className="text-sm text-muted-foreground">
            Progress: {sentEmails.length}/{emailTests.length} emails sent
          </div>
        )}
      </CardContent>
    </Card>
  );
}